import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, CheckCircle2, Phone, Mail } from 'lucide-react';
import SEOHead from '../components/SEOHead';

export default function JobsPage() {
  const jobBenefits = [
    'Firmabil og telefon',
    'Løn efter kvalifikationer',
    'Pension og sundhedsforsikring efter overenskomst',
    'Tandforsikring',
    'Fleksible arbejdstider',
    'Et stærkt fællesskab og et godt arbejdsmiljø',
    'Mulighed for at være med til at bygge noget, der er på vej op',
  ];

  const jobRequirements = [
    'Er uddannet elektriker',
    'Har solid erfaring i faget',
    'Tager ansvar og kan arbejde selvstændigt',
    'Har faglig stolthed og går op i kvalitet',
    'Trives i et stærkt team og bidrager til fællesskabet',
    'Har kørekort (B)',
  ];

  const jobTasks = [
    'Elinstallationer i større projekter',
    'Tavler og forsyning',
    'Energioptimering',
    'Intelligent lysstyring',
    'Service og installation i større erhvervsløsninger',
  ];

  return (
    <main data-testid="jobs-page" className="pt-24">
      <SEOHead path="/ledige-stillinger" />
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-16 h-16 flex items-center justify-center mb-8">
                <Briefcase size={48} strokeWidth={1} className="text-[#0E3D24]" />
              </div>
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                Ledige stillinger
              </span>
              <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#0E3D24] mt-4 leading-[1.1]">
                Erfaren elektriker søges til større eltekniske projekter på Sjælland
              </h1>
              <p className="text-[#1E2A24]/70 text-lg mt-6 leading-relaxed max-w-xl">
                Vi bygger noget, vi selv tror på bliver rigtig godt. Ikke bare flere opgaver eller flere biler på vejen – men en virksomhed med et stærkt hold, høj faglighed og fokus på elinstallationer, der kræver mere end standardarbejde.
              </p>
              <a 
                href="mailto:info@dilling-el.dk?subject=Ansøgning: Erfaren elektriker"
                className="inline-flex items-center gap-3 mt-10 bg-[#0E3D24] text-white px-8 py-4 text-sm tracking-[0.05em] hover:bg-[#1F5A3A] transition-colors group"
              >
                <span>Send ansøgning</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="aspect-[4/3] bg-[#0E3D24] flex items-center justify-center overflow-hidden">
                <img loading="lazy" 
                  src="/images/film-grabs/still-5.jpg" 
                  alt="Dilling EL team"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Om stillingen */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                Om stillingen
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mt-6">
                Det her er ikke et sted for en, der bare vil passe sin bil og køre små opgaver hele dagen
              </h2>
              <p className="text-[#1E2A24]/70 mt-6 leading-relaxed">
                Vi søger en erfaren elektriker, der stadig går op i kvalitet, ordentlighed og godt håndværk – og som gerne vil være med i en virksomhed, hvor man bygger noget sammen.
              </p>
              <p className="text-[#1E2A24]/70 mt-4 leading-relaxed">
                Du kommer primært til at arbejde med elinstallationer i større bygninger, haller og erhvervsprojekter på Sjælland. Det her er for dem, der gerne vil arbejde med ordentlige projekter, stærke kollegaer og en virksomhed med ambitioner.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                Opgaver
              </span>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-[#0E3D24] mt-6 mb-6">
                Dine arbejdsopgaver
              </h3>
              <div className="space-y-4">
                {jobTasks.map((task, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <CheckCircle2 size={20} className="text-[#0E3D24] mt-0.5 flex-shrink-0" />
                    <span className="text-[#1E2A24]/70">{task}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#1E2A24]/50 text-sm mt-6 italic">
                Fælles for opgaverne er, at de kræver overblik, faglighed og godt håndværk.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Krav */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
              Hvem søger vi
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mt-6">
              Vi leder efter en, der...
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobRequirements.map((req, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 border border-[#A6B8A2]/30"
              >
                <div className="flex items-start gap-4">
                  <CheckCircle2 size={24} className="text-[#0E3D24] flex-shrink-0" />
                  <span className="text-[#1E2A24]">{req}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fordele */}
      <section className="py-24 md:py-32 bg-[#0E3D24]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-[11px] tracking-[0.2em] uppercase text-white/50">
              Fordele
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-white mt-6">
              Hos Dilling EL får du
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {jobBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-4 py-4 border-b border-white/10"
              >
                <CheckCircle2 size={20} className="text-[#A6B8A2] flex-shrink-0" />
                <span className="text-white/80">{benefit}</span>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-white/50 mt-12 max-w-2xl"
          >
            Vi prioriterer ikke kun arbejdet – men også at skabe et sted, hvor folk har lyst til at være. Med gode kollegaer, fællesskab og oplevelser undervejs.
          </motion.p>
        </div>
      </section>

      {/* Kontakt / CTA */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                Interesseret?
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mt-6">
                Kan du se dig selv i det her?
              </h2>
              <p className="text-[#1E2A24]/70 mt-6 leading-relaxed">
                Så vil vi gerne høre fra dig. Samtaler afholdes løbende, og vi ansætter, når den rigtige dukker op.
              </p>
              
              <div className="mt-10 space-y-4">
                <div className="flex items-center gap-4">
                  <Phone size={20} className="text-[#0E3D24]" />
                  <div>
                    <p className="text-[#1E2A24]/50 text-sm">Kontakt Caspar</p>
                    <a href="tel:+4546461234" className="text-[#0E3D24] font-medium hover:text-[#1F5A3A] transition-colors">
                      46 46 12 34
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Mail size={20} className="text-[#0E3D24]" />
                  <div>
                    <p className="text-[#1E2A24]/50 text-sm">Email</p>
                    <a href="mailto:info@dilling-el.dk" className="text-[#0E3D24] font-medium hover:text-[#1F5A3A] transition-colors">
                      info@dilling-el.dk
                    </a>
                  </div>
                </div>
              </div>

              <a 
                href="mailto:info@dilling-el.dk?subject=Ansøgning: Erfaren elektriker"
                className="inline-flex items-center gap-3 mt-10 bg-[#0E3D24] text-white px-8 py-4 text-sm tracking-[0.05em] hover:bg-[#1F5A3A] transition-colors group"
              >
                <span>Send din ansøgning</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="aspect-square md:aspect-square bg-[#0E3D24] overflow-hidden relative">
                <video 
                  loop
                  muted
                  playsInline
                  controls
                  preload="metadata"
                  poster="/videos/team-video-poster.jpg"
                  className="w-full h-full object-cover"
                >
                  <source src="/videos/team-video.mp4" type="video/mp4" />
                </video>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
